// Le Répertoire — the app's front door. The left tree IS the repo's taxonomy;
// a row is a PROCESS (file and/or cycle record), never a database row.
// Layout per ux-design §2; classes per the kit spec (toolbar/chips/table).
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, ExternalLink } from 'lucide-react';
import { api } from '../api/client';
import { GithubAccount } from '../components/GithubAccount';
import { useRole } from '../context/RoleContext';
import { useSearch } from '../context/SearchContext';
import { useT } from '../i18n';
import { agoLabel } from '../utils/format';
import { NewProcessModal } from '../components/NewProcessModal';

export interface CatalogueRow {
  kind: 'file' | 'draft';
  path: string | null;
  tier: string;
  areaSegments: string[];
  title: string;
  variant: string | null;
  variantParts?: string[];
  familyKey: string;
  lanes: string[] | null;
  laneCount: number;
  isEmpty: boolean;
  isTechnical: boolean;
  isDuplicate: boolean;
  duplicateKind?: 'identical' | 'divergent' | null;
  duplicateOf: string[];
  parseError: string | null;
  lastCommit: { sha: string; author: string; date: string } | null;
  record: null | {
    id: number; process_code: string; state: string; kind: string; in_inventory: boolean;
    phase_key: string; phase_order: number; phase_label_fr: string; phase_label_en: string;
    owner_name: string | null; functions: string[]; superseded_by?: string | null; roleCount: number; openTickets: number;
  };
  openTickets: number;
}

type Filter = 'all' | 'live' | 'cycle' | 'documented' | 'tomodel' | 'tickets';

/** Row state, in the répertoire's five-value vocabulary. */
function rowState(r: CatalogueRow): 'live' | 'cycle' | 'documented' | 'tomodel' | 'draft' | 'superseded' {
  if (r.kind === 'draft') return 'draft';
  if (r.record) {
    if (r.record.state === 'superseded') return 'superseded';
    // "En service" means the process is actually live (phase 8) — NOT merely
    // accepted into the inventory at phase 5. Those are different facts.
    if (r.record.state === 'live' || r.record.phase_key === 'complete') return 'live';
    return 'cycle';
  }
  return r.isEmpty ? 'tomodel' : 'documented';
}

function StateChip({ r, t, lang }: { r: CatalogueRow; t: any; lang: string }) {
  const state = rowState(r);
  if (r.parseError) return <span className="state-chip state-chip--warn" title={r.parseError}>{t('rep.state.unreadable')}</span>;
  switch (state) {
    case 'live': return <span className="state-chip state-chip--live">{t('rep.state.live')}</span>;
    case 'superseded': return <span className="state-chip state-chip--superseded">{r.record?.superseded_by ? t('rep.state.superseded', { code: r.record.superseded_by }) : t('rep.state.supersededBare')}</span>;
    case 'cycle': return (
      <span className="state-chip state-chip--cycle">
        {t('rep.state.step', { n: r.record!.phase_order })} · {lang === 'fr' ? r.record!.phase_label_fr : r.record!.phase_label_en}
      </span>
    );
    case 'documented': return <span className="state-chip state-chip--documented" title={t('rep.documentedTip')}>{t('rep.state.documented')}</span>;
    case 'tomodel': return <span className="state-chip state-chip--tomodel" title={t('rep.toModelTip')}>{t('rep.state.toModel')}</span>;
    case 'draft': return <span className="state-chip state-chip--draft" title={t('rep.draftTip')}>{t('rep.state.draft')}</span>;
  }
}

interface TreeNode { key: string; label: string; depth: number; count: number; children: TreeNode[] }

function buildTree(rows: CatalogueRow[], rootLabel: string, t: any): TreeNode {
  const root: TreeNode = { key: '', label: rootLabel, depth: 0, count: rows.length, children: [] };
  const ensure = (parent: TreeNode, key: string, label: string, depth: number): TreeNode => {
    let node = parent.children.find(c => c.key === key);
    if (!node) { node = { key, label, depth, count: 0, children: [] }; parent.children.push(node); }
    return node;
  };
  for (const r of rows) {
    const tier = ensure(root, r.tier, r.tier, 1);
    tier.count++;
    let parent = tier;
    let key = r.tier;
    if (r.areaSegments.length === 0 && r.kind === 'file') {
      const rootNode = ensure(tier, `${key}//`, t('rep.root'), 2);
      rootNode.count++;
      continue;
    }
    r.areaSegments.forEach((seg, i) => {
      key = `${key}/${seg}`;
      parent = ensure(parent, key, seg, 2 + i);
      parent.count++;
    });
  }
  const sortRec = (n: TreeNode) => { n.children.sort((a, b) => a.label.localeCompare(b.label)); n.children.forEach(sortRec); };
  sortRec(root);
  return root;
}

export function RepertoirePage() {
  const { t, lang } = useT();
  const { isCde } = useRole();
  const { searchTerm } = useSearch();
  const [data, setData] = useState<{ repo: any; rows: CatalogueRow[] } | null>(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedNode, setSelectedNode] = useState('');
  const [groupVersions, setGroupVersions] = useState(true);
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const [showTechnical, setShowTechnical] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = () => api.getCatalogue().then(setData).catch(() => setError(true));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.getRepoStatus().then(() => {}).catch(() => {});
    fetch('/api/admin/settings', { credentials: 'include' }).then(r => r.ok ? r.json() : null)
      .then(s => { if (s?.show_technical_files === 'true') setShowTechnical(true); }).catch(() => {});
  }, []);

  const rows = data?.rows ?? [];
  const visible = useMemo(() => {
    let out = rows.filter(r => showTechnical || !r.isTechnical);
    if (selectedNode) {
      out = out.filter(r => {
        const path = [r.tier, ...r.areaSegments].join('/');
        if (selectedNode.endsWith('//')) return r.tier === selectedNode.slice(0, -2) && r.areaSegments.length === 0;
        return path === selectedNode || path.startsWith(selectedNode + '/');
      });
    }
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      out = out.filter(r =>
        r.title.toLowerCase().includes(q) ||
        (r.path ?? '').toLowerCase().includes(q) ||
        (r.record?.process_code ?? '').toLowerCase().includes(q) ||
        (r.record?.owner_name ?? '').toLowerCase().includes(q) ||
        (r.lanes ?? []).some(l => l.toLowerCase().includes(q))
      );
    }
    if (filter !== 'all') {
      out = out.filter(r => {
        const s = rowState(r);
        if (filter === 'live') return s === 'live';
        if (filter === 'cycle') return s === 'cycle' || s === 'draft';
        if (filter === 'documented') return s === 'documented';
        if (filter === 'tomodel') return s === 'tomodel';
        if (filter === 'tickets') return r.openTickets > 0;
        return true;
      });
    }
    return out;
  }, [rows, selectedNode, searchTerm, filter, showTechnical]);

  // version families: one row per family, most-advanced variant leads
  const grouped = useMemo(() => {
    if (!groupVersions) return visible.map(r => ({ lead: r, variants: [] as CatalogueRow[] }));
    const families = new Map<string, CatalogueRow[]>();
    for (const r of visible) families.set(r.familyKey, [...(families.get(r.familyKey) ?? []), r]);
    const rank = (r: CatalogueRow) => (r.record ? 100 + r.record.phase_order : 0)
      + (r.variant?.startsWith('TO-BE') ? 50 : 0)
      + (Number((r.variant?.match(/V(\d+)/) ?? [])[1]) || 0);
    return [...families.values()].map(members => {
      const sorted = [...members].sort((a, b) => rank(b) - rank(a));
      return { lead: sorted[0], variants: sorted.slice(1) };
    });
  }, [visible, groupVersions]);

  // group rows by tier, then area, preserving repo order
  const sections = useMemo(() => {
    const byTier = new Map<string, Map<string, typeof grouped>>();
    for (const g of grouped) {
      const area = g.lead.areaSegments.join(' / ') || t('rep.root');
      if (!byTier.has(g.lead.tier)) byTier.set(g.lead.tier, new Map());
      const tierMap = byTier.get(g.lead.tier)!;
      tierMap.set(area, [...(tierMap.get(area) ?? []), g]);
    }
    return [...byTier.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [grouped, t]);

  const tree = useMemo(
    () => buildTree(rows.filter(r => showTechnical || !r.isTechnical), t('rep.allProcesses'), t),
    [rows, showTechnical, t]
  );

  const open = (r: CatalogueRow) => {
    if (r.record) window.location.hash = `#/process/${r.record.id}`;
    else if (r.path) window.location.hash = `#/process/file?path=${encodeURIComponent(r.path)}`;
  };

  const syncedAgo = () => {
    if (!data?.repo?.builtAt) return '';
    const mins = Math.floor((Date.now() - new Date(data.repo.builtAt).getTime()) / 60000);
    return mins < 1 ? t('rep.justNow') : t('rep.minutesAgo', { n: mins });
  };

  if (error) return <div className="empty-state"><div className="empty-state-title">{t('rep.repoDown')}</div></div>;
  if (!data) return <div className="page-loading"><div className="spinner" /></div>;

  const renderNode = (node: TreeNode): React.ReactNode => (
    <div key={node.key || 'root'}>
      <button
        className={`rep-tree-node${node.depth >= 2 ? ` rep-tree-indent-${Math.min(node.depth - 1, 2)}` : ''}${selectedNode === node.key ? ' active' : ''}`}
        onClick={() => setSelectedNode(node.key)}
      >
        <span>{node.label}</span>
        <span className="rep-tree-count">{node.count}</span>
      </button>
      {node.children.map(renderNode)}
    </div>
  );

  const filterChips: { key: Filter; label: string }[] = [
    { key: 'all', label: t('rep.filter.all') },
    { key: 'live', label: t('rep.filter.live') },
    { key: 'cycle', label: t('rep.filter.cycle') },
    { key: 'documented', label: t('rep.filter.documented') },
    { key: 'tomodel', label: t('rep.filter.toModel') },
    { key: 'tickets', label: t('rep.filter.tickets') },
  ];

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('shell.title.repository')}</h1>
          <p className="page-meta">{t('rep.count', { n: grouped.length })}</p>
        </div>
        <div className="flex items-center gap-16" style={{ flexWrap: 'wrap' }}>
          <GithubAccount variant="inline" />
          {isCde && (
            <button className="btn btn-accent" onClick={() => setCreating(true)}>
              <Plus className="icon-sm" /> {t('rep.new')}
            </button>
          )}
        </div>
      </div>

      {!data.repo.configured && (
        <div className="empty-state-card mb-16"><div className="empty-state">
          <div className="empty-state-desc">{t('rep.repoDown')}</div>
        </div></div>
      )}

      <div className="toolbar">
        <div className="filter-chips">
          {filterChips.map(f => (
            <button key={f.key} className={`chip ${filter === f.key ? 'active' : ''}`}
                    onClick={() => setFilter(filter === f.key ? 'all' : f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <label className="toggle toggle-compact">
          <input type="checkbox" className="toggle-input" checked={groupVersions} onChange={e => setGroupVersions(e.target.checked)} />
          <span className="toggle-slider" />
          <span className="toggle-label">{t('rep.groupVersions')}</span>
        </label>
      </div>

      <div className="rep-layout">
        <aside className="rep-tree">
          {renderNode({ ...tree, key: '' })}
          <div className="rep-foot">
            {data.repo.configured && (
              <>
                <span>{t('rep.branch', { branch: 'main' })}</span>
                <span>{t('rep.syncedAgo', { when: syncedAgo() })}</span>
                <a className="text-accent" href="https://github.com/Mr-Moonsilver/eveil-global-processes" target="_blank" rel="noreferrer">
                  {t('rep.openGithub')} <ExternalLink className="icon-inline" size={10} />
                </a>
              </>
            )}
          </div>
        </aside>

        <div>
          {visible.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-title">{searchTerm ? t('rep.noResults', { q: searchTerm }) : t('rep.emptyDomain')}</div>
              <div className="empty-state-desc">{searchTerm ? t('rep.noResultsHint') : ''}</div>
              {!searchTerm && isCde && (
                <button className="btn btn-accent mt-16" onClick={() => setCreating(true)}>
                  <Plus className="icon-sm" /> {t('rep.emptyDomainCta')}
                </button>
              )}
            </div>
          ) : sections.map(([tier, areas]) => (
            <section key={tier} className="mb-24">
              <div className="rep-tier-head"><span>{tier}</span></div>
              {[...areas.entries()].map(([area, groups]) => (
                <div key={area}>
                  {area !== t('rep.root') && <div className="rep-area-head"><span>{area}</span></div>}
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: 20 }} />
                        <th>{t('rep.col.process')}</th>
                        <th style={{ width: 190 }}>{t('rep.col.state')}</th>
                        <th style={{ width: 150 }}>{t('rep.col.owner')}</th>
                        <th style={{ width: 190 }}>{t('rep.col.functions')}</th>
                        <th style={{ width: 70 }}>{t('rep.col.roles')}</th>
                        <th style={{ width: 130 }}>{t('rep.col.modified')}</th>
                        <th style={{ width: 60 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map(g => {
                        const r = g.lead;
                        const expanded = expandedFamilies.has(r.familyKey);
                        return (
                          <FragmentRows
                            key={(r.path ?? '') + (r.record?.id ?? '')}
                            group={g} expanded={expanded}
                            toggle={() => setExpandedFamilies(s => {
                              const next = new Set(s);
                              next.has(r.familyKey) ? next.delete(r.familyKey) : next.add(r.familyKey);
                              return next;
                            })}
                            open={open} t={t} lang={lang}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>

      {creating && (
        <NewProcessModal
          defaultNode={selectedNode}
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); window.location.hash = `#/process/${id}`; }}
        />
      )}
    </>
  );
}

function FragmentRows({ group, expanded, toggle, open, t, lang }: {
  group: { lead: CatalogueRow; variants: CatalogueRow[] };
  expanded: boolean;
  toggle: () => void;
  open: (r: CatalogueRow) => void;
  t: any; lang: string;
}) {
  const r = group.lead;
  const hasVariants = group.variants.length > 0;
  const members = expanded ? group.variants : [];

  return (
    <>
      <tr className="list-row-clickable" onClick={() => open(r)}>
        <td style={{ width: 20, paddingRight: 0 }}>
          {hasVariants && (
            <button className="btn-icon" onClick={e => { e.stopPropagation(); toggle(); }}>
              {expanded ? <ChevronDown className="icon-sm" /> : <ChevronRight className="icon-sm" />}
            </button>
          )}
        </td>
        <td>
          <span className="font-medium text-ink-1">{r.title}</span>{' '}
          {hasVariants && group.variants.map(v => (
            <button key={v.path} className="variant-chip" title={v.path ?? ''}
                    onClick={e => { e.stopPropagation(); open(v); }}>
              {v.variant ?? '—'}
            </button>
          ))}
          {!hasVariants && r.variant && <span className="variant-chip on" style={{ cursor: 'default' }}>{r.variant}</span>}
          {r.isDuplicate && (
            <span className={`state-chip ${r.duplicateKind === 'divergent' ? 'state-chip--warn' : 'state-chip--documented'}`}
                  title={t('rep.chip.duplicateTip', { paths: r.duplicateOf.join(' · ') })}>
              {r.duplicateKind === 'divergent' ? t('rep.chip.duplicateDivergent') : t('rep.chip.duplicate')}
            </span>
          )}
          {r.record?.kind === 'rework' && <span className="badge">{t('fiche.p1.kind.rework')}</span>}
          <div className="rep-path">{r.path ?? t('rep.draftTip')}</div>
        </td>
        <td style={{ width: 190 }}><StateChip r={r} t={t} lang={lang} /></td>
        <td style={{ width: 150 }} className="text-sm">{r.record?.owner_name ?? '—'}</td>
        <td style={{ width: 190 }}>
          {(r.record?.functions?.length ?? 0) > 0
            ? r.record!.functions.map(f => <span key={f} className="badge">{f}</span>)
            : (r.lanes ?? []).slice(0, 3).map(l => <span key={l} className="ghost-chip" title={t('fiche.lanes')}>{l}</span>)}
        </td>
        <td style={{ width: 70 }} className="text-sm text-ink-3">
          {r.laneCount > 0 ? t('rep.rolesCount', { n: r.laneCount }) : '—'}
        </td>
        <td style={{ width: 130 }} className="text-sm text-ink-3">
          {r.lastCommit ? <span title={r.lastCommit.author}>{agoLabel(r.lastCommit.date, lang)}</span> : '—'}
        </td>
        <td style={{ width: 60 }}>
          {r.openTickets > 0 && <span className="badge badge-warn">{r.openTickets}</span>}
        </td>
      </tr>
      {members.map(v => (
        <tr key={v.path} className="list-row-clickable" onClick={() => open(v)}>
          <td />
          <td style={{ paddingLeft: 'var(--s24)' }}>
            <span className="text-sm">{v.title}</span>{' '}
            {v.variant && <span className="variant-chip on" style={{ cursor: 'default' }}>{v.variant}</span>}
            <div className="rep-path">{v.path}</div>
          </td>
          <td><StateChip r={v} t={t} lang={lang} /></td>
          <td className="text-sm">{v.record?.owner_name ?? '—'}</td>
          <td />
          <td className="text-sm text-ink-3">{v.laneCount > 0 ? t('rep.rolesCount', { n: v.laneCount }) : '—'}</td>
          <td className="text-sm text-ink-3">{v.lastCommit ? agoLabel(v.lastCommit.date, lang) : '—'}</td>
          <td />
        </tr>
      ))}
    </>
  );
}
